import {create} from 'zustand';
import {DEFAULT_ITEM_SORT, ITEM_SORTS, type ItemSort} from '@/api/items';

export type SubmissionScope = 'mine' | 'all';
export type ItemsView = 'grid' | 'list' | 'detailed';
// A slider level from smallest (most columns) to largest (one item per row).
export type ThumbnailSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
// The submissions table's own thumbnail size - separate from ThumbnailSize, which is a grid column
// count, so resizing the item grid doesn't also resize every table row.
export const SUBMISSIONS_THUMBNAIL_SIZES = ['sm', 'md', 'lg'] as const;
export type SubmissionsThumbnailSize = (typeof SUBMISSIONS_THUMBNAIL_SIZES)[number];

const SUBMISSION_SCOPE_KEY = 'submissionScope';
const SUBMISSIONS_THUMBNAIL_SIZE_KEY = 'submissionsThumbnailSize';
const THUMBNAIL_SIZE_KEY = 'thumbnailSize';
const BLUR_THUMBNAILS_KEY = 'blurThumbnails';
const GREYSCALE_THUMBNAILS_KEY = 'greyscaleThumbnails';
const ITEMS_VIEW_KEY = 'itemsView';
const DASHBOARD_HIDDEN_VERTICALS_KEY = 'dashboardHiddenVerticals';
const INVESTIGATIONS_RAIL_WIDTH_KEY = 'investigationsRailWidth';
const ITEM_DETAILS_WIDTH_KEY = 'itemDetailsWidth';
const ITEM_FILTERS_KEY = 'investigationItemFiltersByJob';
const SMART_FILTER_KEY = 'investigationSmartFilterByJob';
const GROUP_BY_CHANNEL_KEY = 'investigationGroupByChannelByJob';
// Ponytail: fixed cap with oldest-wins eviction (plain-object insertion order), not a real LRU
// structure - fine at this size, revisit if analysts routinely work 50+ investigations in the
// same browser profile. Shared by every per-investigation map below.
const PER_INVESTIGATION_MAX_KEYS = 50;
export const THUMBNAIL_SIZE_MIN: ThumbnailSize = 1;
export const THUMBNAIL_SIZE_MAX: ThumbnailSize = 10;
const THUMBNAIL_SIZE_DEFAULT: ThumbnailSize = 5;
// Pixels, from the Investigations rail design: 360 keeps the three filters and the Mine · All toggle
// legible on one row each, and 560 stops the rail swallowing the item grid.
export const INVESTIGATIONS_RAIL_WIDTH_MIN = 360;
export const INVESTIGATIONS_RAIL_WIDTH_MAX = 560;
export const INVESTIGATIONS_RAIL_WIDTH_DEFAULT = 360;
// Pixels, for the item details panel: 320 still fits the metadata list's two columns, and the most is
// 42rem (the scale's 2xl container), about a third of a 1920px screen, which still leaves the grid a
// column or two beside it on a laptop.
export const ITEM_DETAILS_WIDTH_MIN = 320;
export const ITEM_DETAILS_WIDTH_MAX = 672;
export const ITEM_DETAILS_WIDTH_DEFAULT = 400;

export type SmartFilterIncludeMode = 'all' | 'any';

export type TriState = '' | 'true' | 'false';

export type RdFilter = {
  signal: string;
  // Canonical JSON scalar text, retained verbatim to preserve numeric precision.
  values: string[];
};

export type ItemFilterState = {
  ageRestrictedFilter: TriState;
  monetizedFilter: TriState;
  verifiedFilter: TriState;
  madeForKidsFilter: TriState;
  isShortFilter: TriState;
  safeForMinorsFilter: TriState;
  aiGeneratedFilter: TriState;
  contentWarningFilter: TriState;
  thumbnailChangedFilter: TriState;
  commentsAllowedFilter: TriState;
  newOnlyFilter: boolean;
  minSubscribersFilter: string;
  minViewsFilter: string;
  maxSubscribersFilter: string;
  maxViewsFilter: string;
  minPinsFilter: string;
  minBoardsFilter: string;
  minSavesFilter: string;
  // Whole minutes.
  minDurationFilter: string;
  maxDurationFilter: string;
  lastPostSinceFilter: string;
  publishedSinceFilter: string;
  publishedUntilFilter: string;
  // BCP-47 base subtags (en, fr) matched against the item's declared language.
  contentLanguageFilter: string[];
  // ISO 3166-1 alpha-2 codes matched against the channel's country.
  contentCountryFilter: string[];
  rdFilters: RdFilter[];
  // Not a filter, but per-investigation view state persisted alongside them.
  sortOrder: ItemSort;
};

export const DEFAULT_ITEM_FILTERS: ItemFilterState = {
  ageRestrictedFilter: '',
  monetizedFilter: '',
  verifiedFilter: '',
  madeForKidsFilter: '',
  isShortFilter: '',
  safeForMinorsFilter: '',
  aiGeneratedFilter: '',
  contentWarningFilter: '',
  thumbnailChangedFilter: '',
  commentsAllowedFilter: '',
  newOnlyFilter: false,
  minSubscribersFilter: '',
  minViewsFilter: '',
  maxSubscribersFilter: '',
  maxViewsFilter: '',
  minPinsFilter: '',
  minBoardsFilter: '',
  minSavesFilter: '',
  minDurationFilter: '',
  maxDurationFilter: '',
  lastPostSinceFilter: '',
  publishedSinceFilter: '',
  publishedUntilFilter: '',
  contentLanguageFilter: [],
  contentCountryFilter: [],
  rdFilters: [],
  sortOrder: DEFAULT_ITEM_SORT
};

export type SmartFilterSettings = {
  enabled: boolean;
  includeMode: SmartFilterIncludeMode;
  includesEnabled: boolean;
  stemming: boolean;
};

export const DEFAULT_SMART_FILTER: SmartFilterSettings = {
  // Absent/default must mean on - smart filtering defaults to enabled.
  enabled: true,
  includeMode: 'any',
  // Include filtering is opt-in: re-requiring the analyst's search words to appear literally can
  // only discard items YouTube already matched, often semantically against a transcript we never
  // see, so it hides wanted content rather than spam.
  includesEnabled: false,
  // The server's `english` FTS config stems, so this is the parity default as well as what an
  // analyst excluding "hello kitty" means for "hello kitties".
  stemming: true
};

export type UiStore = {
  submissionScope: SubmissionScope;
  thumbnailSize: ThumbnailSize;
  submissionsThumbnailSize: SubmissionsThumbnailSize;
  blurThumbnails: boolean;
  greyscaleThumbnails: boolean;
  // Grid, compact list or detailed list - one choice shared by Items and Investigations.
  itemsView: ItemsView;
  // The Investigations left rail, in pixels - dragged by the analyst and remembered per browser.
  investigationsRailWidth: number;
  itemDetailsWidth: number;
  // Dashboard verticals the analyst has unticked - stored as hidden rather than shown so a vertical
  // that becomes deliverable later appears instead of staying hidden.
  dashboardHiddenVerticals: ReadonlySet<string>;
  // All three keyed by `job:<id>` / `folder:<id>` (see
  // InvestigationsWorkspace) - each investigation remembers its own filters, smart-filter settings
  // and grouped/flat view rather than sharing one global set.
  itemFiltersByKey: Record<string, ItemFilterState>;
  smartFilterByKey: Record<string, SmartFilterSettings>;
  groupByChannelByKey: Record<string, boolean>;
  setSubmissionScope: (scope: SubmissionScope) => void;
  setThumbnailSize: (size: ThumbnailSize) => void;
  setSubmissionsThumbnailSize: (size: SubmissionsThumbnailSize) => void;
  setBlurThumbnails: (enabled: boolean) => void;
  setGreyscaleThumbnails: (enabled: boolean) => void;
  setItemsView: (view: ItemsView) => void;
  setInvestigationsRailWidth: (width: number) => void;
  setItemDetailsWidth: (width: number) => void;
  setDashboardHiddenVerticals: (hidden: ReadonlySet<string>) => void;
  setItemFilters: (key: string, patch: Partial<ItemFilterState>) => void;
  setSmartFilter: (key: string, patch: Partial<SmartFilterSettings>) => void;
  setGroupByChannel: (key: string, enabled: boolean) => void;
};

function readSubmissionScope(): SubmissionScope {
  try {
    return localStorage.getItem(SUBMISSION_SCOPE_KEY) === 'all' ? 'all' : 'mine';
  } catch {
    return 'mine';
  }
}

function readThumbnailSize(): ThumbnailSize {
  try {
    const value = Number(localStorage.getItem(THUMBNAIL_SIZE_KEY));
    return Number.isInteger(value) && value >= THUMBNAIL_SIZE_MIN && value <= THUMBNAIL_SIZE_MAX
      ? (value as ThumbnailSize)
      : THUMBNAIL_SIZE_DEFAULT;
  } catch {
    return THUMBNAIL_SIZE_DEFAULT;
  }
}

function readSubmissionsThumbnailSize(): SubmissionsThumbnailSize {
  try {
    const value = localStorage.getItem(SUBMISSIONS_THUMBNAIL_SIZE_KEY);
    return SUBMISSIONS_THUMBNAIL_SIZES.find((size) => size === value) ?? 'md';
  } catch {
    return 'md';
  }
}

function readItemsView(): ItemsView {
  try {
    const stored = localStorage.getItem(ITEMS_VIEW_KEY);
    return stored === 'list' || stored === 'detailed' ? stored : 'grid';
  } catch {
    return 'grid';
  }
}

function readDashboardHiddenVerticals(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(DASHBOARD_HIDDEN_VERTICALS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
    );
  } catch {
    return new Set();
  }
}

function clampRailWidth(width: number): number {
  return Math.round(
    Math.min(INVESTIGATIONS_RAIL_WIDTH_MAX, Math.max(INVESTIGATIONS_RAIL_WIDTH_MIN, width))
  );
}

function clampItemDetailsWidth(width: number): number {
  return Math.round(Math.min(ITEM_DETAILS_WIDTH_MAX, Math.max(ITEM_DETAILS_WIDTH_MIN, width)));
}

function readItemDetailsWidth(): number {
  try {
    const value = Number(localStorage.getItem(ITEM_DETAILS_WIDTH_KEY));
    return value > 0 ? clampItemDetailsWidth(value) : ITEM_DETAILS_WIDTH_DEFAULT;
  } catch {
    return ITEM_DETAILS_WIDTH_DEFAULT;
  }
}

function readInvestigationsRailWidth(): number {
  try {
    const value = Number(localStorage.getItem(INVESTIGATIONS_RAIL_WIDTH_KEY));
    return value > 0 ? clampRailWidth(value) : INVESTIGATIONS_RAIL_WIDTH_DEFAULT;
  } catch {
    return INVESTIGATIONS_RAIL_WIDTH_DEFAULT;
  }
}

function readBlurThumbnails(): boolean {
  try {
    // Absent key must mean on - thumbnails blur by default. Analysts should not have to take the
    // imagery just to read an item's metadata, so revealing is the deliberate act, not concealing.
    return localStorage.getItem(BLUR_THUMBNAILS_KEY) !== '0';
  } catch {
    return true;
  }
}

function readGreyscaleThumbnails(): boolean {
  try {
    // Unlike blur, greyscale is an opt-in welfare preference, so an absent key means off.
    return localStorage.getItem(GREYSCALE_THUMBNAILS_KEY) === '1';
  } catch {
    return false;
  }
}

// Superseded by SMART_FILTER_KEY (global -> per-investigation) - dead, but left behind in
// existing browsers unless swept.
function sweepStaleSmartFilterKeys(): void {
  try {
    localStorage.removeItem('smartFilter');
    localStorage.removeItem('smartFilterIncludeMode');
    localStorage.removeItem('smartFilterIncludes');
    localStorage.removeItem('smartFilterStemming');
  } catch {
    /* ignore storage errors */
  }
}

function isTriState(value: unknown): value is TriState {
  return value === '' || value === 'true' || value === 'false';
}

function isSmartFilterIncludeMode(value: unknown): value is SmartFilterIncludeMode {
  return value === 'all' || value === 'any';
}

// BuildFilterParams (InvestigationsWorkspace.tsx) does Math.trunc(Number(...)) on the two numeric
// fields, so a stored value that isn't a plain non-negative integer string would become NaN and
// 400 the whole /api/items request - validated here rather than trusted from storage.
function isItemSort(value: unknown): value is ItemSort {
  return typeof value === 'string' && (ITEM_SORTS as readonly string[]).includes(value);
}

export function sanitizeItemFilters(parsed: Partial<ItemFilterState>): ItemFilterState {
  return {
    ageRestrictedFilter: isTriState(parsed.ageRestrictedFilter)
      ? parsed.ageRestrictedFilter
      : DEFAULT_ITEM_FILTERS.ageRestrictedFilter,
    monetizedFilter: isTriState(parsed.monetizedFilter)
      ? parsed.monetizedFilter
      : DEFAULT_ITEM_FILTERS.monetizedFilter,
    verifiedFilter: isTriState(parsed.verifiedFilter)
      ? parsed.verifiedFilter
      : DEFAULT_ITEM_FILTERS.verifiedFilter,
    madeForKidsFilter: isTriState(parsed.madeForKidsFilter)
      ? parsed.madeForKidsFilter
      : DEFAULT_ITEM_FILTERS.madeForKidsFilter,
    isShortFilter: isTriState(parsed.isShortFilter)
      ? parsed.isShortFilter
      : DEFAULT_ITEM_FILTERS.isShortFilter,
    safeForMinorsFilter: isTriState(parsed.safeForMinorsFilter)
      ? parsed.safeForMinorsFilter
      : DEFAULT_ITEM_FILTERS.safeForMinorsFilter,
    aiGeneratedFilter: isTriState(parsed.aiGeneratedFilter)
      ? parsed.aiGeneratedFilter
      : DEFAULT_ITEM_FILTERS.aiGeneratedFilter,
    contentWarningFilter: isTriState(parsed.contentWarningFilter)
      ? parsed.contentWarningFilter
      : DEFAULT_ITEM_FILTERS.contentWarningFilter,
    thumbnailChangedFilter: isTriState(parsed.thumbnailChangedFilter)
      ? parsed.thumbnailChangedFilter
      : DEFAULT_ITEM_FILTERS.thumbnailChangedFilter,
    commentsAllowedFilter: isTriState(parsed.commentsAllowedFilter)
      ? parsed.commentsAllowedFilter
      : DEFAULT_ITEM_FILTERS.commentsAllowedFilter,
    newOnlyFilter:
      typeof parsed.newOnlyFilter === 'boolean'
        ? parsed.newOnlyFilter
        : DEFAULT_ITEM_FILTERS.newOnlyFilter,
    minSubscribersFilter:
      typeof parsed.minSubscribersFilter === 'string' && /^\d*$/.test(parsed.minSubscribersFilter)
        ? parsed.minSubscribersFilter
        : DEFAULT_ITEM_FILTERS.minSubscribersFilter,
    minViewsFilter:
      typeof parsed.minViewsFilter === 'string' && /^\d*$/.test(parsed.minViewsFilter)
        ? parsed.minViewsFilter
        : DEFAULT_ITEM_FILTERS.minViewsFilter,
    maxSubscribersFilter:
      typeof parsed.maxSubscribersFilter === 'string' && /^\d*$/.test(parsed.maxSubscribersFilter)
        ? parsed.maxSubscribersFilter
        : DEFAULT_ITEM_FILTERS.maxSubscribersFilter,
    maxViewsFilter:
      typeof parsed.maxViewsFilter === 'string' && /^\d*$/.test(parsed.maxViewsFilter)
        ? parsed.maxViewsFilter
        : DEFAULT_ITEM_FILTERS.maxViewsFilter,
    minPinsFilter:
      typeof parsed.minPinsFilter === 'string' && /^\d*$/.test(parsed.minPinsFilter)
        ? parsed.minPinsFilter
        : DEFAULT_ITEM_FILTERS.minPinsFilter,
    minBoardsFilter:
      typeof parsed.minBoardsFilter === 'string' && /^\d*$/.test(parsed.minBoardsFilter)
        ? parsed.minBoardsFilter
        : DEFAULT_ITEM_FILTERS.minBoardsFilter,
    minSavesFilter:
      typeof parsed.minSavesFilter === 'string' && /^\d*$/.test(parsed.minSavesFilter)
        ? parsed.minSavesFilter
        : DEFAULT_ITEM_FILTERS.minSavesFilter,
    minDurationFilter:
      typeof parsed.minDurationFilter === 'string' && /^\d*$/.test(parsed.minDurationFilter)
        ? parsed.minDurationFilter
        : DEFAULT_ITEM_FILTERS.minDurationFilter,
    maxDurationFilter:
      typeof parsed.maxDurationFilter === 'string' && /^\d*$/.test(parsed.maxDurationFilter)
        ? parsed.maxDurationFilter
        : DEFAULT_ITEM_FILTERS.maxDurationFilter,
    lastPostSinceFilter:
      parsed.lastPostSinceFilter === '' ||
      (typeof parsed.lastPostSinceFilter === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(parsed.lastPostSinceFilter))
        ? parsed.lastPostSinceFilter
        : DEFAULT_ITEM_FILTERS.lastPostSinceFilter,
    publishedSinceFilter:
      parsed.publishedSinceFilter === '' ||
      (typeof parsed.publishedSinceFilter === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(parsed.publishedSinceFilter))
        ? parsed.publishedSinceFilter
        : DEFAULT_ITEM_FILTERS.publishedSinceFilter,
    publishedUntilFilter:
      parsed.publishedUntilFilter === '' ||
      (typeof parsed.publishedUntilFilter === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(parsed.publishedUntilFilter))
        ? parsed.publishedUntilFilter
        : DEFAULT_ITEM_FILTERS.publishedUntilFilter,
    contentLanguageFilter: Array.isArray(parsed.contentLanguageFilter)
      ? parsed.contentLanguageFilter.filter((code) => typeof code === 'string' && code !== '')
      : DEFAULT_ITEM_FILTERS.contentLanguageFilter,
    contentCountryFilter: Array.isArray(parsed.contentCountryFilter)
      ? parsed.contentCountryFilter.filter((code) => typeof code === 'string' && code !== '')
      : DEFAULT_ITEM_FILTERS.contentCountryFilter,
    rdFilters: Array.isArray(parsed.rdFilters)
      ? parsed.rdFilters.filter((group) => {
          if (
            !group ||
            typeof group.signal !== 'string' ||
            !group.signal ||
            !Array.isArray(group.values)
          ) {
            return false;
          }

          return group.values.every((value) => {
            if (typeof value !== 'string') {
              return false;
            }

            try {
              const scalar: unknown = JSON.parse(value);
              return scalar !== null && ['string', 'number', 'boolean'].includes(typeof scalar);
            } catch {
              return false;
            }
          });
        })
      : [],
    sortOrder: isItemSort(parsed.sortOrder) ? parsed.sortOrder : DEFAULT_ITEM_FILTERS.sortOrder
  };
}

function sanitizeSmartFilter(parsed: Partial<SmartFilterSettings>): SmartFilterSettings {
  return {
    enabled: typeof parsed.enabled === 'boolean' ? parsed.enabled : DEFAULT_SMART_FILTER.enabled,
    includeMode: isSmartFilterIncludeMode(parsed.includeMode)
      ? parsed.includeMode
      : DEFAULT_SMART_FILTER.includeMode,
    includesEnabled:
      typeof parsed.includesEnabled === 'boolean'
        ? parsed.includesEnabled
        : DEFAULT_SMART_FILTER.includesEnabled,
    stemming: typeof parsed.stemming === 'boolean' ? parsed.stemming : DEFAULT_SMART_FILTER.stemming
  };
}

// Shared by the object-valued per-investigation maps (item filters, smart filter settings) -
// reads the raw
// JSON object at `storageKey` and sanitizes every entry, dropping anything malformed rather than
// trusting hand-edited or corrupted localStorage.
function readMapByKey<T>(
  storageKey: string,
  sanitize: (parsed: Partial<T>) => T
): Record<string, T> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as Record<string, Partial<T>>;
    if (typeof parsed !== 'object' || parsed === null) {
      return {};
    }

    const result: Record<string, T> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'object' && value !== null) {
        result[key] = sanitize(value);
      }
    }

    return result;
  } catch {
    return {};
  }
}

// The per-investigation grouping flag is a bare boolean rather than a settings object, so
// readMapByKey's object-valued entries don't apply - same posture though, dropping any entry that
// isn't a boolean instead of trusting hand-edited or corrupted localStorage.
function readBooleanMapByKey(storageKey: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) {
      return {};
    }

    const result: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'boolean') {
        result[key] = value;
      }
    }

    return result;
  } catch {
    return {};
  }
}

// Shared by every per-investigation map - upserts `key` (moving it to the end, since omitting
// then reassigning changes plain-object insertion order) and evicts the oldest key once over cap.
function upsertCapped<T>(map: Record<string, T>, key: string, value: T): Record<string, T> {
  const {[key]: _current, ...rest} = map;
  let next: Record<string, T> = {...rest, [key]: value};

  const oldestKey = Object.keys(next)[0];
  if (Object.keys(next).length > PER_INVESTIGATION_MAX_KEYS && oldestKey !== undefined) {
    const {[oldestKey]: _oldest, ...trimmed} = next;
    next = trimmed;
  }

  return next;
}

sweepStaleSmartFilterKeys();

export const useUiStore = create<UiStore>((set) => ({
  submissionScope: readSubmissionScope(),
  thumbnailSize: readThumbnailSize(),
  submissionsThumbnailSize: readSubmissionsThumbnailSize(),
  blurThumbnails: readBlurThumbnails(),
  greyscaleThumbnails: readGreyscaleThumbnails(),
  itemsView: readItemsView(),
  investigationsRailWidth: readInvestigationsRailWidth(),
  itemDetailsWidth: readItemDetailsWidth(),
  dashboardHiddenVerticals: readDashboardHiddenVerticals(),
  itemFiltersByKey: readMapByKey(ITEM_FILTERS_KEY, sanitizeItemFilters),
  smartFilterByKey: readMapByKey(SMART_FILTER_KEY, sanitizeSmartFilter),
  groupByChannelByKey: readBooleanMapByKey(GROUP_BY_CHANNEL_KEY),

  setSubmissionScope(scope: SubmissionScope) {
    try {
      localStorage.setItem(SUBMISSION_SCOPE_KEY, scope);
    } catch {
      /* ignore storage errors */
    }

    set({submissionScope: scope});
  },

  setThumbnailSize(size: ThumbnailSize) {
    try {
      localStorage.setItem(THUMBNAIL_SIZE_KEY, String(size));
    } catch {
      /* ignore storage errors */
    }

    set({thumbnailSize: size});
  },

  setSubmissionsThumbnailSize(size: SubmissionsThumbnailSize) {
    try {
      localStorage.setItem(SUBMISSIONS_THUMBNAIL_SIZE_KEY, size);
    } catch {
      /* ignore storage errors */
    }

    set({submissionsThumbnailSize: size});
  },

  setItemsView(view: ItemsView) {
    try {
      localStorage.setItem(ITEMS_VIEW_KEY, view);
    } catch {
      /* ignore storage errors */
    }

    set({itemsView: view});
  },

  setInvestigationsRailWidth(width: number) {
    const clamped = clampRailWidth(width);
    try {
      localStorage.setItem(INVESTIGATIONS_RAIL_WIDTH_KEY, String(clamped));
    } catch {
      /* ignore storage errors */
    }

    set({investigationsRailWidth: clamped});
  },

  setItemDetailsWidth(width: number) {
    const clamped = clampItemDetailsWidth(width);
    try {
      localStorage.setItem(ITEM_DETAILS_WIDTH_KEY, String(clamped));
    } catch {
      /* ignore storage errors */
    }

    set({itemDetailsWidth: clamped});
  },

  setDashboardHiddenVerticals(hidden: ReadonlySet<string>) {
    try {
      localStorage.setItem(DASHBOARD_HIDDEN_VERTICALS_KEY, JSON.stringify([...hidden]));
    } catch {
      /* ignore storage errors */
    }

    set({dashboardHiddenVerticals: hidden});
  },

  setBlurThumbnails(enabled: boolean) {
    try {
      localStorage.setItem(BLUR_THUMBNAILS_KEY, enabled ? '1' : '0');
    } catch {
      /* ignore storage errors */
    }

    set({blurThumbnails: enabled});
  },

  setGreyscaleThumbnails(enabled: boolean) {
    try {
      localStorage.setItem(GREYSCALE_THUMBNAILS_KEY, enabled ? '1' : '0');
    } catch {
      /* ignore storage errors */
    }

    set({greyscaleThumbnails: enabled});
  },

  setItemFilters(key: string, patch: Partial<ItemFilterState>) {
    set((state) => {
      const current = state.itemFiltersByKey[key] ?? DEFAULT_ITEM_FILTERS;
      const next = upsertCapped(state.itemFiltersByKey, key, {...current, ...patch});
      try {
        localStorage.setItem(ITEM_FILTERS_KEY, JSON.stringify(next));
      } catch {
        /* ignore storage errors */
      }

      return {itemFiltersByKey: next};
    });
  },

  setSmartFilter(key: string, patch: Partial<SmartFilterSettings>) {
    set((state) => {
      const current = state.smartFilterByKey[key] ?? DEFAULT_SMART_FILTER;
      const next = upsertCapped(state.smartFilterByKey, key, {...current, ...patch});
      try {
        localStorage.setItem(SMART_FILTER_KEY, JSON.stringify(next));
      } catch {
        /* ignore storage errors */
      }

      return {smartFilterByKey: next};
    });
  },

  setGroupByChannel(key: string, enabled: boolean) {
    set((state) => {
      const next = upsertCapped(state.groupByChannelByKey, key, enabled);
      try {
        localStorage.setItem(GROUP_BY_CHANNEL_KEY, JSON.stringify(next));
      } catch {
        /* ignore storage errors */
      }

      return {groupByChannelByKey: next};
    });
  }
}));

/** Reset all UI state. Test-only — keeps unit tests independent of order. */
export function __resetUiStore(): void {
  useUiStore.setState({
    submissionScope: 'mine',
    thumbnailSize: THUMBNAIL_SIZE_DEFAULT,
    submissionsThumbnailSize: 'md',
    blurThumbnails: true,
    greyscaleThumbnails: false,
    itemsView: 'grid',
    investigationsRailWidth: INVESTIGATIONS_RAIL_WIDTH_DEFAULT,
    itemDetailsWidth: ITEM_DETAILS_WIDTH_DEFAULT,
    dashboardHiddenVerticals: new Set(),
    itemFiltersByKey: {},
    smartFilterByKey: {},
    groupByChannelByKey: {}
  });
}
